import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { FloppyDisk, Eye, FilePdf, FileXls, ArrowCounterClockwise, GraduationCap, CheckCircle, XCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { logError } from '@/lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const currentMonth = () => new Date().toISOString().slice(0, 7);
const COMPANIES = ['Tulsi Jobs', 'National Institute of Learning', 'Tulsi Makeup & Fitness Studio'];
const numCls = "w-16 bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded px-1.5 py-1 text-sm font-bold text-center";

const computeNet = (d) => {
  const perDay = Math.round(((parseFloat(d.basic_salary) || 0) / 30) * 100) / 100;
  const payable = Math.max((parseFloat(d.total_days) || 30) - (parseFloat(d.absent_days) || 0), 0);
  const earned = d.on_training ? 0 : Math.round(perDay * payable * 100) / 100;
  const extras = (parseFloat(d.travelling_allowance) || 0) + (parseFloat(d.other_payments) || 0) - (parseFloat(d.deductions) || 0);
  return { perDay, earned, net: Math.round((earned + extras) * 100) / 100 };
};

const SalaryRow = ({ rec, onSaved }) => {
  const [d, setD] = useState({ ...rec, net_input: '' });
  const [dirty, setDirty] = useState(false);
  const [netEdited, setNetEdited] = useState(false);

  useEffect(() => { setD({ ...rec, net_input: '' }); setDirty(false); setNetEdited(false); }, [rec]);

  const { perDay, net } = computeNet(d);
  const displayNet = netEdited ? d.net_input : (rec.net_override != null && !dirty ? rec.net_salary : net);
  const hasOverride = rec.net_override != null && !netEdited;

  const set = (k, v) => { setD((p) => ({ ...p, [k]: v })); setDirty(true); };

  const save = async () => {
    const payload = {
      total_days: parseFloat(d.total_days) || 30,
      absent_days: parseFloat(d.absent_days) || 0,
      other_payments: parseFloat(d.other_payments) || 0,
      deductions: parseFloat(d.deductions) || 0,
      basic_salary: parseFloat(d.basic_salary) || 0,
      travelling_allowance: parseFloat(d.travelling_allowance) || 0,
      on_training: !!d.on_training,
    };
    if (netEdited && d.net_input !== '') payload.net_override = parseFloat(d.net_input) || 0;
    try {
      await axios.put(`${API}/salary/${rec.id}`, payload);
      toast.success(`Saved ${rec.staff_name}`);
      onSaved();
    } catch (err) {
      logError('salary-save', err);
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const resetOverride = async () => {
    try {
      await axios.put(`${API}/salary/${rec.id}`, { clear_net_override: true });
      toast.success('Net salary reset to auto-calculated');
      onSaved();
    } catch (err) {
      logError('salary-reset', err);
      toast.error('Failed to reset');
    }
  };

  const togglePaid = async () => {
    try {
      await axios.put(`${API}/salary/${rec.id}`, { paid: !rec.paid });
      toast.success(!rec.paid ? `${rec.staff_name} marked PAID` : `${rec.staff_name} marked UNPAID`);
      onSaved();
    } catch (err) {
      logError('salary-paid', err);
      toast.error('Failed to update payment status');
    }
  };

  const openPayslip = async (download) => {
    try {
      const res = await axios.get(`${API}/salary/${rec.id}/payslip.pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (download) {
        const a = document.createElement('a');
        a.href = url;
        a.download = `TulsiJobs_PaySlip_${rec.staff_name.replace(/\s+/g, '_')}_${rec.month}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success(`Pay slip downloaded for ${rec.staff_name}`);
      } else {
        const win = window.open(url, '_blank', 'noopener,noreferrer');
        if (!win) toast.error('Pop-up blocked — allow pop-ups to preview');
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      }
    } catch (err) {
      logError('salary-payslip', err);
      toast.error('Failed to generate pay slip');
    }
  };

  return (
    <tr data-testid={`salary-row-${rec.id}`} className={dirty || netEdited ? 'bg-[#14432B]' : ''}>
      <td className="font-bold">
        {rec.staff_name}
        <div className="text-[10px] font-normal text-[#FDE047]">{rec.company || 'Tulsi Jobs'}</div>
        {rec.designation && <div className="text-[10px] font-normal text-[#A3E635]">{rec.designation}</div>}
      </td>
      <td>
        <button
          data-testid={`salary-training-${rec.id}`}
          onClick={() => set('on_training', !d.on_training)}
          title={d.on_training ? 'On training — only travelling allowance paid' : 'Click to mark on training this month'}
          className={`px-2 py-1 border-2 border-black rounded text-[10px] font-bold flex items-center gap-1 ${d.on_training ? 'bg-[#FDE047] text-black' : 'bg-[#E5E5E5] text-[#555]'}`}
        >
          <GraduationCap size={12} weight="bold" />
          {d.on_training ? 'TRAINING' : 'NO'}
        </button>
      </td>
      <td>
        <input data-testid={`salary-basic-${rec.id}`} type="number" min="0" className={`${numCls} w-24`} value={d.basic_salary} onChange={(e) => set('basic_salary', e.target.value)} />
      </td>
      <td>
        <input data-testid={`salary-ta-${rec.id}`} type="number" min="0" className={`${numCls} w-20`} value={d.travelling_allowance} onChange={(e) => set('travelling_allowance', e.target.value)} />
      </td>
      <td>
        <input data-testid={`salary-other-${rec.id}`} type="number" min="0" title="Other payments (bonus, incentive, etc.)" className={`${numCls} w-20`} value={d.other_payments ?? 0} onChange={(e) => set('other_payments', e.target.value)} />
      </td>
      <td>
        <input data-testid={`salary-deduct-${rec.id}`} type="number" min="0" title="Deductions (advance, penalty, etc.) — subtracted from net" className={`${numCls} w-20 ${parseFloat(d.deductions) > 0 ? 'border-[#F87171]' : ''}`} value={d.deductions ?? 0} onChange={(e) => set('deductions', e.target.value)} />
      </td>
      <td>
        <input data-testid={`salary-totaldays-${rec.id}`} type="number" min="0" max="31" className={numCls} value={d.total_days ?? 30} onChange={(e) => set('total_days', e.target.value)} />
      </td>
      <td>
        <input data-testid={`salary-absent-${rec.id}`} type="number" min="0" max="31" className={numCls} value={d.absent_days} onChange={(e) => set('absent_days', e.target.value)} />
      </td>
      <td className="text-xs font-bold text-white text-center" title="LOP days are fixed at 4 (not deducted)">4</td>
      <td className="text-xs font-bold text-[#93C5FD]">{inr(perDay)}</td>
      <td>
        <div className="flex items-center gap-1">
          <input
            data-testid={`salary-net-${rec.id}`}
            type="number"
            min="0"
            title="Auto-calculated. Type here to override the amount before printing."
            className={`${numCls} w-24 ${hasOverride || netEdited ? 'border-[#FDE047] bg-[#FEF9C3]' : ''}`}
            value={displayNet}
            onChange={(e) => { setD((p) => ({ ...p, net_input: e.target.value })); setNetEdited(true); }}
          />
          {rec.net_override != null && (
            <button data-testid={`salary-reset-net-${rec.id}`} onClick={resetOverride} title="Reset to auto-calculated amount" className="p-1 bg-[#93C5FD] border-2 border-black rounded">
              <ArrowCounterClockwise size={12} weight="bold" />
            </button>
          )}
        </div>
      </td>
      <td>
        <div className="flex flex-col gap-1 items-start">
          <button
            data-testid={`salary-paid-${rec.id}`}
            onClick={togglePaid}
            title={rec.paid ? 'Click to mark unpaid' : 'Click to mark salary as paid (today)'}
            className={`px-2 py-1.5 border-2 border-black rounded text-[10px] font-bold flex items-center gap-1 transition-all hover:-translate-y-0.5 ${rec.paid ? 'bg-[#A3E635] text-black' : 'bg-[#F87171] text-white'}`}
          >
            {rec.paid ? <CheckCircle size={13} weight="bold" /> : <XCircle size={13} weight="bold" />}
            {rec.paid ? 'PAID' : 'UNPAID'}
          </button>
          {rec.paid && (
            <input
              data-testid={`salary-paid-date-${rec.id}`}
              type="date"
              title="Salary paid date — change if paid on a different day"
              value={(rec.paid_at || '').slice(0, 10)}
              onChange={async (e) => {
                if (!e.target.value) return;
                try {
                  await axios.put(`${API}/salary/${rec.id}`, { paid_date: e.target.value });
                  toast.success('Paid date updated');
                  onSaved();
                } catch (err) {
                  logError('salary-paid-date', err);
                  toast.error('Failed to update paid date');
                }
              }}
              className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded px-1 py-0.5 text-[10px] font-bold w-[110px]"
            />
          )}
        </div>
      </td>
      <td>
        <div className="flex gap-1.5">
          <button
            data-testid={`salary-save-${rec.id}`}
            onClick={save}
            title="Save attendance & salary"
            className={`px-2.5 py-2 border-2 border-black rounded text-xs font-bold flex items-center gap-1 transition-all hover:-translate-y-0.5 ${dirty || netEdited ? 'bg-[#A3E635] text-black' : 'bg-[#E5E5E5] text-[#555]'}`}
          >
            <FloppyDisk size={14} weight="bold" />
            Save
          </button>
          <button data-testid={`salary-payslip-view-${rec.id}`} onClick={() => openPayslip(false)} title="Preview pay slip" className="p-2 bg-[#93C5FD] border-2 border-black rounded hover:-translate-y-0.5 transition-all">
            <Eye size={14} weight="bold" />
          </button>
          <button data-testid={`salary-payslip-dl-${rec.id}`} onClick={() => openPayslip(true)} title="Download pay slip PDF" className="p-2 bg-[#FDE047] border-2 border-black rounded hover:-translate-y-0.5 transition-all">
            <FilePdf size={14} weight="bold" />
          </button>
        </div>
      </td>
    </tr>
  );
};

export const SheetTab = () => {
  const [month, setMonth] = useState(currentMonth());
  const [company, setCompany] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSheet = useCallback(async () => {
    setLoading(true);
    try {
      const params = { month };
      if (company) params.company = company;
      const { data: d } = await axios.get(`${API}/salary`, { params });
      setData(d);
    } catch (e) {
      logError('salary-sheet', e);
      toast.error('Failed to load salary sheet');
    } finally {
      setLoading(false);
    }
  }, [month, company]);

  useEffect(() => { fetchSheet(); }, [fetchSheet]);

  const exportXlsx = async () => {
    try {
      const params = { month };
      if (company) params.company = company;
      const res = await axios.get(`${API}/salary-export.xlsx`, { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `TulsiJobs_Salary_${month}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Salary sheet exported');
    } catch (err) {
      logError('salary-export', err);
      toast.error('Failed to export');
    }
  };

  return (
    <div data-testid="salary-sheet-tab">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <input
          data-testid="salary-month-input"
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2 font-bold"
        />
        <select
          data-testid="salary-company-filter"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2 font-bold text-sm"
        >
          <option value="">All Companies</option>
          {COMPANIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {data && (
          <div data-testid="salary-total-net" className="bg-[#FDE047] text-black border-2 border-black rounded-md px-4 py-2 font-black shadow-[2px_2px_0px_0px_rgba(10,10,10,1)]">
            Total Payout: {inr(data.total_net)} · {data.staff_count} staff · {data.paid_count ?? 0} paid
          </div>
        )}
        <div className="flex-1" />
        <Button
          data-testid="salary-export-button"
          onClick={exportXlsx}
          className="bg-[#A3E635] text-black border-2 border-black rounded-md font-bold px-4 py-2 shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-all"
        >
          <FileXls size={18} weight="bold" className="mr-2" />
          Export Excel
        </Button>
      </div>

      {loading ? (
        <div className="text-white font-bold">Loading…</div>
      ) : !data || data.records.length === 0 ? (
        <div className="bg-[#0F3622] border-2 border-[#A3E635] rounded-md p-8 text-center text-white font-bold">
          No staff added yet. Go to the <strong>Staff</strong> tab and add your employees first — they will appear here automatically every month.
        </div>
      ) : (
        <div className="bg-[#0F3622] border-2 border-[#A3E635] rounded-md shadow-[4px_4px_0px_0px_rgba(163,230,53,0.3)] overflow-x-auto">
          <table className="w-full tracker-table" data-testid="salary-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Training</th>
                <th>Basic ₹</th>
                <th>Travel ₹</th>
                <th>Other ₹</th>
                <th>Deduct ₹</th>
                <th>Total Days</th>
                <th>Absent</th>
                <th>LOP</th>
                <th>Per-Day</th>
                <th>Net Salary</th>
                <th>Paid</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.records.map((rec) => (
                <SalaryRow key={rec.id} rec={rec} onSaved={fetchSheet} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-[#A3E635] mt-3 font-medium">
        Formula: Per-day = Basic ÷ 30 · Net = Per-day × (Total Days − Absent) + Travelling + Other Payments − Deductions · LOP fixed at 4 days (not deducted) · Training month = travelling + other − deductions. Per-Day shows on the portal only, not on the pay slip. Edit the Net amount directly to override before printing.
      </p>
    </div>
  );
};

export default SheetTab;
